import { ServerErrorResponse } from '../utils/errors';
import log from 'loglevel';

export interface GroupCollection {
    groups: Group[],
    isComplete: boolean
}

export interface Group {
    azureGroupId: string,
    azureGroupName: string,
    azureGroupType: string
}

async function executeJsonMethodOnApi(authentication: any, method: string, relativeApiPath: string, jsonBody: any = null): Promise<any> {
    const response = await executeMethodOnApi(authentication, method, relativeApiPath, jsonBody);
    return await response.json();
}

async function executeBlobMethodOnApi(authentication: any, method: string, relativeApiPath: string, jsonBody: any = null): Promise<Blob> {
    const response = await executeMethodOnApi(authentication, method, relativeApiPath, jsonBody);
    return await response.blob();
}

async function executeMethodOnApi(authentication: any, method: string, relativeApiPath: string , bodyData: any = null): Promise<Response> {
    try {
        log.debug(`[executeCallOnBackend] Invoking '${method}' on '${relativeApiPath}'...`)

        const accessToken = await authentication.getApiTokenAsync();
    
        const url = `/api/${relativeApiPath}`;
        
        const requestHeaders: HeadersInit = new Headers();
        requestHeaders.set('Authorization', `Bearer ${accessToken.accessToken}`);

        const requestParameters: RequestInit = {
            method: method,
            headers: requestHeaders
        };
    
        // Add the body if one was provided.
        if (bodyData) {
            if (bodyData instanceof FormData)
            {
                requestParameters.body = bodyData;
            }
            else
            {
                requestHeaders.set('Content-Type', 'application/json');
                requestParameters.body = JSON.stringify(bodyData);
            }
        }
    
        const response = await fetch(url, requestParameters);
    
        if (!response.ok) {
            // Some non-success response received.
            throw new ServerErrorResponse(null, response.status, response.statusText, await response.text());
        }
    
        // A success response is received from the back-end.
        return response;
    }
    catch (error: any) {
        if (error instanceof ServerErrorResponse) {
            const responseBody = error.responseBody ? `\r\n${error.responseBody}` : "";
            log.error(`Server error response received while invoking '${method}' on '/${relativeApiPath}' endpoint. Received ${error.statusCode} '${error.statusText}'.${responseBody}`);
        } else {
            log.error(`Error occurred while invoking '${method}' on '/${relativeApiPath}': ${error.message}`);    
        }

        throw (error);
    }
}

export async function getAvailableTenants(authentication: any): Promise<any> {
    return await executeJsonMethodOnApi(authentication, 'GET', `tenant`, null);
}

export async function createTenant(authentication: any, tenantLabel: string): Promise<any> {
    const tenantInfo = {
        tenantLabel: tenantLabel,
    };
    return await executeMethodOnApi(authentication, 'POST', `tenant`, tenantInfo);
}

export async function getTenant(authentication: any, tenantId: string): Promise<any> {
    return await executeJsonMethodOnApi(authentication, 'GET', `tenant/${tenantId}`, null);
}

export async function tenantUpdateAuditTrailRetention(authentication: any, tenantId: string, auditTrailRetentionDays: number): Promise<any> {
    const tenantDto = {
        auditTrailRetentionDays: auditTrailRetentionDays,
    }

    return await executeMethodOnApi(authentication, 'PATCH', `tenant/${tenantId}`, tenantDto);
}

export async function getSubscriptions(authentication: any, tenantId: string): Promise<any> {
    return await executeJsonMethodOnApi(authentication, 'GET', `tenant/${tenantId}/subscription`, null);
}
    
export async function getDevices(authentication: any, tenantId: string): Promise<any> {
    return await executeJsonMethodOnApi(authentication, 'GET', `tenant/${tenantId}/deviceold`, null);
}

export async function addDevice(authentication: any, tenantId: string, name: string, description: string, groupId?: number): Promise<any> {
    var deviceDto = {
        deviceName: name,
        deviceDescription: description,
        groupId: groupId,
    };

    return await executeMethodOnApi(authentication, 'POST', `tenant/${tenantId}/device`, deviceDto);
}

export async function updateDevice(authentication: any, tenantId: string, deviceId: number, name: string, description: string, groupId?: number): Promise<any> {
    var deviceDto = {
        deviceName: name,
        deviceDescription: description,
        groupId: groupId,
    };

    return await executeMethodOnApi(authentication, 'PATCH', `tenant/${tenantId}/device/${deviceId}`, deviceDto);
}

export async function deleteDevice(authentication: any, tenantId: string, deviceId: number): Promise<any> {
    return await executeMethodOnApi(authentication, 'DELETE', `tenant/${tenantId}/device/${deviceId}`, null);
}

export async function getUsers(authentication: any, tenantId: string): Promise<any> {
    return await executeJsonMethodOnApi(authentication, 'GET', `tenant/${tenantId}/webrtcuser`, null);
}

export async function addUser(authentication: any, tenantId: string, name: string, description: string): Promise<any> {
    var userDto = {
        name: name,
        description: description,
    };

    return await executeMethodOnApi(authentication, 'POST', `tenant/${tenantId}/webrtcuser`, userDto);
}

export async function updateUser(authentication: any, tenantId: string, userId: number, name: string, description: string): Promise<any> {
    var userDto = {
        name: name,
        description: description,
    };

    return await executeMethodOnApi(authentication, 'PATCH', `tenant/${tenantId}/webrtcuser/${userId}`, userDto);
}

export async function deleteUser(authentication: any, tenantId: string, userId: number): Promise<any> {
    return await executeMethodOnApi(authentication, 'DELETE', `tenant/${tenantId}/webrtcuser/${userId}`, null);
}

export async function getTokens(authentication: any, tenantId: string, userId: number): Promise<any> {
    return await executeJsonMethodOnApi(authentication, 'GET', `tenant/${tenantId}/webrtcuser/${userId}/token`, null);
}

export async function addToken(authentication: any, tenantId: string, userId: number, fcmToken: string, deviceName: string): Promise<any> {
    var tokenDto = {
        fcmToken: fcmToken,
        deviceName: deviceName
    };

    return await executeMethodOnApi(authentication, 'POST', `tenant/${tenantId}/webrtcuser/${userId}/token`, tokenDto);
}

export async function deleteToken(authentication: any, tenantId: string, userId: number, tokenId: number): Promise<any> {
    return await executeMethodOnApi(authentication, 'DELETE', `tenant/${tenantId}/webrtcuser/${userId}/token/${tokenId}`, null);
}

export async function getContactGroups(authentication: any, tenantId: string): Promise<any> {
    return await executeJsonMethodOnApi(authentication, 'GET', `tenant/${tenantId}/contactgroup`, null);
}

export async function addContactGroup(authentication: any, tenantId: string, name: string, description: string): Promise<any> {
    var contactGroupDto = {
        name: name,
        description: description,
    };

    return await executeMethodOnApi(authentication, 'POST', `tenant/${tenantId}/contactgroup`, contactGroupDto);
}

export async function updateContactGroup(authentication: any, tenantId: string, groupId: number, name: string, description: string): Promise<any> {
    var contactGroupDto = {
        name: name,
        description: description,
    };

    return await executeMethodOnApi(authentication, 'PATCH', `tenant/${tenantId}/contactgroup/${groupId}`, contactGroupDto);
}

export async function deleteContactGroup(authentication: any, tenantId: string, groupId: number): Promise<any> {
    return await executeMethodOnApi(authentication, 'DELETE', `tenant/${tenantId}/contactgroup/${groupId}`, null);
}

export async function addContactGroupEntry(authentication: any, tenantId: string, groupId: number, userId: number): Promise<any> {
    var contactAssignDto = {
        userId: userId,
    };

    return await executeMethodOnApi(authentication, 'POST',  `tenant/${tenantId}/contactgroup/${groupId}/Entries`, contactAssignDto);
}

export async function deleteContactGroupEntry(authentication: any, tenantId: string, groupId: number, userId: number): Promise<any> {
    return await executeMethodOnApi(authentication, 'DELETE', `tenant/${tenantId}/contactgroup/${groupId}/Entries/${userId}`, null);
}

export async function getPortalAccess(authentication: any, tenantId: string): Promise<Array<Group>> {
    return await executeJsonMethodOnApi(authentication, 'GET', `tenant/${tenantId}/portalaccess`, null);
}

export async function addPortalAccess(authentication: any, tenantId: string, azureGroupId: string, azureGroupName: string, azureGroupType: string): Promise<any> {
    var portalAccessDto = {
        azureGroupId: azureGroupId,
        azureGroupName: azureGroupName,
        azureGroupType: azureGroupType
    }

    return await executeMethodOnApi(authentication, 'POST', `tenant/${tenantId}/portalaccess`, portalAccessDto);
}

export async function deletePortalAccess(authentication: any, tenantId: string, azureGroupId: string): Promise<any> {
    return await executeMethodOnApi(authentication, 'DELETE', `tenant/${tenantId}/portalaccess/${azureGroupId}`, null);
}

export async function getAvailableGroups(authentication: any, tenantId: string): Promise<GroupCollection> {
    return await executeJsonMethodOnApi(authentication, 'GET', `tenant/${tenantId}/groups`, null);
}

export async function getAuditTrail(authentication: any, tenantId: string, pageSize: number, pageNumber: number): Promise<any> {
    return await executeJsonMethodOnApi(authentication, 'GET', `tenant/${tenantId}/audittrail?pageSize=${pageSize}&pageNumber=${pageNumber}`, null);
}

export async function getAuditTrailForUser(authentication: any, tenantId: string, userId: string, pageSize: number, pageNumber: number): Promise<any> {
    return await executeJsonMethodOnApi(authentication, 'GET', `tenant/${tenantId}/audittrail/${userId}?pageSize=${pageSize}&pageNumber=${pageNumber}`, null);
}

export async function getAuditTrailUsers(authentication: any, tenantId: string): Promise<any> {
    return await executeJsonMethodOnApi(authentication, 'GET', `tenant/${tenantId}/audittrail/users`, null);
}

export async function getOpenApiSpec(authentication: any): Promise<any> {
    return await executeJsonMethodOnApi(authentication, 'GET', `swagger/v1/swagger.json`, null);
}
